import { ArrowRight, ChevronDown, Minus as MinusIcon, Plus, Tag, X } from "lucide-react";
import { formatPrice } from "@/lib/formatPrice";

interface BulkDiscountPanelProps {
  selectedCount: number;
  discountPercent: string;
  onDiscountPercentChange: (value: string) => void;
  roundTo: "none" | "floor" | "ceil";
  onRoundToChange: (value: "none" | "floor" | "ceil") => void;
  preview: { original: number; discounted: number; name: string } | null;
  onApply: () => void;
  onClose: () => void;
  isPending: boolean;
}

export default function BulkDiscountPanel({
  selectedCount,
  discountPercent,
  onDiscountPercentChange,
  roundTo,
  onRoundToChange,
  preview,
  onApply,
  onClose,
  isPending,
}: BulkDiscountPanelProps) {
  return (
    <div className="relative rounded-xl bg-[#1a1a1a] text-white overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />

      <div className="relative p-5 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="size-9 rounded-lg bg-white/10 flex items-center justify-center">
              <Tag className="size-4.5 text-amber-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold tracking-wide">Grupni popust</h3>
              <p className="text-[11px] text-white/50 mt-0.5">
                {selectedCount} {selectedCount === 1 ? "proizvod odabran" : "proizvoda odabrano"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="size-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
          >
            <X className="size-4 text-white/60" />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-5">
          <div className="flex-1 space-y-4">
            <div>
              <label className="text-[11px] font-medium text-white/40 uppercase tracking-widest mb-2 block">
                Procenat popusta
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onDiscountPercentChange(String(Math.max(1, (parseFloat(discountPercent) || 0) - 5)))}
                  className="size-10 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors shrink-0"
                >
                  <MinusIcon className="size-4" />
                </button>
                <div className="relative flex-1 max-w-[140px]">
                  <input
                    type="number"
                    min="1"
                    max="99"
                    value={discountPercent}
                    onChange={(e) => onDiscountPercentChange(e.target.value)}
                    className="w-full h-12 rounded-lg bg-white/5 border border-white/10 text-center text-2xl font-bold tabular-nums text-white focus:border-amber-400/60 focus:ring-1 focus:ring-amber-400/30 outline-none transition-all"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-white/30">%</span>
                </div>
                <button
                  onClick={() => onDiscountPercentChange(String(Math.min(99, (parseFloat(discountPercent) || 0) + 5)))}
                  className="size-10 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors shrink-0"
                >
                  <Plus className="size-4" />
                </button>
              </div>
              <div className="flex gap-1.5 mt-2">
                {[10, 20, 30, 50].map((p) => (
                  <button
                    key={p}
                    onClick={() => onDiscountPercentChange(String(p))}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                      discountPercent === String(p)
                        ? "bg-amber-400 text-[#1a1a1a]"
                        : "bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/70"
                    }`}
                  >
                    {p}%
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[11px] font-medium text-white/40 uppercase tracking-widest mb-2 block">
                Zaokruživanje cijene
              </label>
              <div className="flex gap-1.5">
                {([
                  { value: "none", label: "Tačno", example: "12.58" },
                  { value: "floor", label: "Na niže", example: "12.00" },
                  { value: "ceil", label: "Na više", example: "13.00" },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => onRoundToChange(opt.value)}
                    className={`flex-1 py-2.5 px-2 rounded-lg text-center transition-all border ${
                      roundTo === opt.value
                        ? "bg-white/10 border-amber-400/50 text-white"
                        : "bg-white/[0.02] border-white/5 text-white/40 hover:border-white/15 hover:text-white/60"
                    }`}
                  >
                    <div className="text-xs font-semibold">{opt.label}</div>
                    <div className="text-[10px] mt-0.5 opacity-60">{opt.example} KM</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:w-64 flex flex-col gap-3">
            {preview ? (
              <div className="flex-1 rounded-lg bg-white/[0.03] border border-white/5 p-4">
                <p className="text-[10px] font-medium text-white/30 uppercase tracking-widest mb-3">Pregled</p>
                <p className="text-xs text-white/50 truncate mb-3">{preview.name}</p>
                <div className="flex items-center gap-2.5 mb-2">
                  <span className="text-sm text-white/40 line-through tabular-nums">
                    {formatPrice(preview.original)}
                  </span>
                  <ArrowRight className="size-3.5 text-amber-400/70" />
                  <span className="text-lg font-bold tabular-nums text-white">
                    {formatPrice(preview.discounted)}
                  </span>
                  <span className="text-xs text-white/40">KM</span>
                </div>
                <div className="inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-400 rounded-md px-2 py-0.5 text-xs font-medium">
                  <ChevronDown className="size-3" />
                  Ušteda {formatPrice(preview.original - preview.discounted)} KM
                </div>
              </div>
            ) : (
              <div className="flex-1 rounded-lg bg-white/[0.03] border border-dashed border-white/10 p-4 flex items-center justify-center">
                <p className="text-xs text-white/20">Unesite procenat</p>
              </div>
            )}

            <button
              onClick={onApply}
              disabled={isPending || !preview}
              className="group relative w-full py-3 rounded-lg bg-amber-400 text-[#1a1a1a] font-semibold text-sm hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all overflow-hidden"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-amber-300 to-amber-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              <span className="relative flex items-center justify-center gap-2">
                {isPending ? (
                  <>
                    <span className="size-4 border-2 border-[#1a1a1a]/20 border-t-[#1a1a1a] rounded-full animate-spin" />
                    Primjenjujem...
                  </>
                ) : (
                  <>
                    <Tag className="size-4" />
                    Primijeni na {selectedCount} {selectedCount === 1 ? "proizvod" : "proizvoda"}
                  </>
                )}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
